import { Directive, ElementRef, ViewChild } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { NBarclaycardCybersource } from '@df/module-barclaycard/barclaycard-cybersource.namespace';
import { BarclaycardCybersourceService } from '@df/module-barclaycard/barclaycard-cybersource.service';
import { NgInject } from '@df/ng/ng-inject';

@Directive()
export abstract class BarclaycardFlexMicroformComponentDef extends AbstractCheckoutPayment<NBarclaycardCybersource.IPaymentMethod> {
    methodCode = NBarclaycardCybersource.EPaymentMethod.chcybersource;

    tokenStatus = new Status();

    /**
     * External script microform class instance
     */
    private microformInstance?: NBarclaycardCybersource.IMicroformInstance;

    /**
     * Validation for input fields handled by external script
     */
    isCardNumberValid = false;
    isCvvValid = false;

    /**
     * Data models for inputs not handled by external script
     */
    creditCardExpMonth?: string;
    creditCardExpYear?: string;
    saveCard = false;
    creditCardType: NBarclaycardCybersource.EMagentoCardTypes;

    /**
     * Authentication status
     */
    private authenticationStatus = new Status();

    private sessionId?: string;

    @NgInject(BarclaycardCybersourceService)
    private barclaycardCybersourceService: BarclaycardCybersourceService;

    @ViewChild('cardinalContainer')
    protected cardinalContainer: ElementRef<any>;

    /**
     * Returns if we want to preload scripts and initiate form on component init
     * @returns {boolean}
     */
    protected override preload(): boolean {
        return true;
    }

    /**
     * On script load success handler
     */
    protected onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        if (this.app.isPlatformBrowser && window.Flex && window.Cardinal) {
            // Get token
            this.app.request.send(
                this.scriptStatus,
                {
                    path: NBarclaycardCybersource.EEndpoints.TokenEndpoint,
                    data: { method: this.methodCode },
                    requestOptions: { method: 'POST' }
                },
                result => {
                    this.subscriptions.add(this.checkoutStepComponent.submittedSubject.subscribe(() => this.detectChanges()));
                    const flexInstance = new window.Flex(result.token);
                    // Create microform instance
                    this.microformInstance = flexInstance.microform({ styles: this.getStyles() });
                    // Set number input field
                    const number = this.microformInstance.createField('number', {
                        placeholder: '1111 2222 3333 4444',
                        autoformat: true
                    });
                    number.load(NBarclaycardCybersource.EInputSelectors.number);
                    number.on('change', (e: NBarclaycardCybersource.IMicroformChangeEvent) => this.cardTypeChangeHandler(e));
                    number.on('change', (e: NBarclaycardCybersource.IMicroformChangeEvent) => this.validationNumberChangeHandler(e));
                    number.on('autocomplete', (e: NBarclaycardCybersource.IMicroformAutofillEvent) => this.cardAutofillHandler(e));
                    // Set CVV/CVW field
                    const securityCode = this.microformInstance.createField('securityCode', { placeholder: '123', autoformat: false });
                    securityCode.load(NBarclaycardCybersource.EInputSelectors.securityCode);
                    securityCode.on('change', (e: NBarclaycardCybersource.IMicroformChangeEvent) => this.validationCvvChangeHandler(e));
                    this.barclaycardCybersourceService.initCardinal(this.isTest);
                },
                () => {
                    this.onScriptLoadError();
                }
            );
        }
    }

    /**
     * Checkout submit handler
     * @param checkoutSubmitBeforeEventData
     * @returns false | void
     */
    protected onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }
        if (!this.authenticationStatus.busy) {
            checkoutSubmitBeforeEventData.continueSubmission = false;
            const status = this.checkoutFormComponent.status;
            if (!this.isExpDateValid()) {
                status.error = 'Incorrect credit card expiration date.';
                return false;
            }
            // If form is valid, prepare data for payment method submission

            const paymentMethodData = this.getSubmissionPaymentData();
            const microformTokenOptions = this.getMicroformTokenOptions();
            if (paymentMethodData && microformTokenOptions) {
                status.busy = true;
                // Get final payment token
                this.microformInstance.createToken(microformTokenOptions, (error, token) => {
                    if (error) {
                        status.error = error.details?.map(i => i?.message || '').join(' ') || Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;
                    } else {
                        // Inject data to submit payload
                        paymentMethodData.additional_data.token = token;
                        checkoutSubmitBeforeEventData.data.paymentMethod = paymentMethodData;
                        const submissionData = checkoutSubmitBeforeEventData.data;

                        const jwt = this.barclaycardCybersourceService.jwtDecode(token);

                        const cardBin = jwt?.data?.number?.substr(0, 6);

                        this.getCardinalSetup(paymentMethodData)
                            .then(response => {
                                paymentMethodData.additional_data.sessionId = response.token;
                                const cardinalContainer = this.cardinalContainer.nativeElement;
                                if (cardinalContainer) {
                                    cardinalContainer.innerHTML = '';
                                }
                                this.barclaycardCybersourceService.initDeviceDataCollection(response.token, this.isTest, cardinalContainer);

                                this.barclaycardCybersourceService
                                    .binDetect(cardBin)
                                    .then(() => {
                                        checkoutSubmitBeforeEventData.next();
                                        this.barclaycardCybersourceService.checkAuthentication(
                                            this.authenticationStatus,
                                            status,
                                            response => {
                                                status.data.busy = false;
                                                const newSubmissionData: Checkout.ISubmissionData = {
                                                    ...submissionData,
                                                    paymentMethod: {
                                                        method: this.methodCode,
                                                        additional_data: {
                                                            cca_response: response
                                                        }
                                                    }
                                                };
                                                this.checkoutFormComponent.submitWithData(newSubmissionData, undefined, undefined, true);
                                                this.authenticationStatus.reset();
                                            },
                                            () => {
                                                status.error = NBarclaycardCybersource.AUTH_ERROR_MESSAGE;
                                                this.authenticationStatus.reset();
                                            }
                                        );
                                    })
                                    .catch(() => {
                                        status.error = Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;
                                    });
                            })
                            .catch(e => {
                                status.error = e || Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;
                            });
                    }
                });
            }
        }
    }

    private getCardinalSetup(
        paymentMethodData: NBarclaycardCybersource.ISubmissionPaymentData
    ): Promise<NBarclaycardCybersource.ISetupCardinalResponse> {
        return new Promise((resolve, reject) => {
            this.barclaycardCybersourceService
                .setupCardinal(paymentMethodData)
                .then(response => {
                    this.sessionId = response.sessionId;
                    resolve(response);
                })
                .catch(reject);
        });
    }

    private get expirationMonth(): string | undefined {
        return this.creditCardExpMonth?.toString().padStart(2, '0');
    }

    private get expirationYear(): string | undefined {
        return this.creditCardExpYear?.toString().padStart(4, '20');
    }

    private getMicroformTokenOptions(): NBarclaycardCybersource.IMicroformTokenOptions | undefined {
        const expirationMonth = this.expirationMonth;
        const expirationYear = this.expirationYear;
        if (expirationMonth && expirationYear) {
            return { expirationYear, expirationMonth };
        }
        return undefined;
    }

    private getSubmissionPaymentData(token?: string, sessionId?: string): NBarclaycardCybersource.ISubmissionPaymentData | undefined {
        // Pad year/month with missing digits while shorter than 4/2
        const expirationMonth = this.expirationMonth;
        const expirationYear = this.expirationYear;
        if (expirationMonth && expirationYear) {
            const data: NBarclaycardCybersource.ISubmissionPaymentAdditionalData = {
                cvv: '',
                ccType: this.creditCardType,
                expDate: expirationMonth + '-' + expirationYear,
                store_cc: this.method.isVaultEnabled && this.saveCard
            };
            if (token) {
                data.token = token;
            }
            if (sessionId) {
                data.sessionId = sessionId;
            }
            return {
                method: this.methodCode,
                additional_data: data,
                ScreenHeight: window.screen.height,
                ScreenWidth: window.screen.width,
                TimeDifference: new Date().getTimezoneOffset(),
                ColorDepth: window.screen.colorDepth,
                JavaEnabled: navigator.javaEnabled(),
                JavaScriptEnabled: typeof window?.document?.createElement === 'function',
                Language: navigator.language,
                AcceptContent: window.navigator.userAgent
            };
        }
        return undefined;
    }

    /////////////////////////////////////////////
    //
    // HELPERS
    //
    /////////////////////////////////////////////

    private get isTest(): boolean {
        return !!this.method?.isTest;
    }

    protected getJsAssets(): string[] {
        return [
            'https://' + (this.isTest ? 'test' : '') + 'flex.cybersource.com/cybersource/assets/microform/0.11/flex-microform.min.js',
            this.isTest
                ? 'https://songbirdstag.cardinalcommerce.com/edge/v1/songbird.js'
                : 'https://songbird.cardinalcommerce.com/edge/v1/songbird.js'
        ];
    }

    /**
     * Converts cybersource cardtypes (001, 002 etc.) to magento ones (VI, MC etc.)
     * @param cybsCardType: string
     * @returns BarclaycardFlexMicroform.MagentoCardTypes | undefined
     */
    private getMagentoCardType(cybsCardType: string): NBarclaycardCybersource.EMagentoCardTypes | undefined {
        return NBarclaycardCybersource.EMagentoCardTypes[cybsCardType];
    }

    /**
     * Returns styles for microform, can be overwritten locally
     * @returns {NBarclaycardFlexMicroform.IMicroformStylesConfig}
     */
    protected getStyles(): NBarclaycardCybersource.IMicroformStylesConfig {
        return {
            input: {
                'font-size': '14px',
                'font-family': 'Gotham, sans-serif',
                color: 'rgb(51,51,51)'
            },
            '::placeholder': {
                color: '#C9CACC'
            },
            ':disabled': { cursor: 'not-allowed' },
            invalid: { color: '#D90912' }
        };
    }

    /////////////////////////////////////////////
    //
    // MICROFORM INPUT EVENT HANDLERS
    //
    /////////////////////////////////////////////

    private validationNumberChangeHandler(event: NBarclaycardCybersource.IMicroformChangeEvent): void {
        this.isCardNumberValid = event.valid;
        this.detectChanges();
    }

    private validationCvvChangeHandler(e: NBarclaycardCybersource.IMicroformChangeEvent): void {
        this.isCvvValid = e.valid ? true : undefined;
        this.detectChanges();
    }

    private cardTypeChangeHandler(e: NBarclaycardCybersource.IMicroformChangeEvent): void {
        this.creditCardType = this.getMagentoCardType(e.card?.[0]?.cybsCardType) || null;
    }

    private cardAutofillHandler(e: NBarclaycardCybersource.IMicroformAutofillEvent): void {
        e?.expirationMonth && (this.creditCardExpMonth = e.expirationMonth);
        e?.expirationYear && (this.creditCardExpYear = e.expirationYear);
    }

    private isExpDateValid(): boolean {
        const currentDate = new Date();
        return !!(this.creditCardExpYear && parseInt(this.creditCardExpYear,10) > currentDate.getFullYear()) || !!(this.creditCardExpMonth && (parseInt(this.creditCardExpMonth,10) >= currentDate.getMonth() + 1));
    }
}
